import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import SearchInput from './common/SearchInput'
import SvRecommendDialog from './common/SvRecommendDialog'
import HighlightText from './common/HighlightText'
import TokenBadge from './common/TokenBadge'
import { filterByText } from '../utils/filterByText'
import { copyToClipboard } from '../utils/clipboard'
import { sql } from '@codemirror/lang-sql'
import { yaml } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { search, searchKeymap, selectSelectionMatches } from '@codemirror/search'
import { keymap } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import { lookml } from '../lang-lookml'
import { api } from '../api'

const LANG_EXT = {
  sql: () => [sql()],
  yaml: () => [yaml()],
  lookml: () => [lookml()],
}

// 모든 사용자의 Semantic View / Composite View / SQL Query 를 한 화면에서 탐색·편집하고
// (a) 원본 소유자 스코프에 그대로 저장하거나 (b) 내 워크스페이스로 복사한다.
//
// 레이아웃: 2-pane (평탄화 목록 | 미리보기/편집).
// 사이드바는 모든 사용자의 항목을 하나의 평면 리스트로 보여준다.
// 각 항목 옆에 작성자(소유자) 이름이 함께 표시된다.
// 우측 패널의 CodeMirror 는 편집 가능. 원본과 다르면 탭 + 헤더에 "수정됨" 표시.
export default function Explore({ storageSettings, myUserId, myUser, isAdmin, showToast, editorTheme }) {
  const [users, setUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  const [viewType, setViewType] = useState('semantic') // 'semantic' | 'composite' | 'sql'

  // type ('semantic'|'composite'|'sql') -> 모든 사용자 합본 flat list
  // (이전 구조: `${user_id}::${type}` -> per-user list — 사용자수 N배 호출 발생)
  // 변경: 단일 집계 엔드포인트 /api/explore/all-views?type=... 한 번 호출.
  const [viewsByType, setViewsByType] = useState({}) // { semantic?: [...], composite?: [...], sql?: [...] }
  const [loadingType, setLoadingType] = useState({}) // { semantic?: bool, composite?: bool, sql?: bool }

  // 선택된 뷰 식별
  const [selectedKey, setSelectedKey] = useState(null) // { userId, viewType, name }
  const [selectedView, setSelectedView] = useState(null) // 서버에서 받은 원본 (불변)
  const [editedView, setEditedView] = useState(null)    // 사용자 편집 작업본 (선택 시 selectedView 사본으로 초기화)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const [previewTab, setPreviewTab] = useState('semantic_yaml')
  const [copyDialog, setCopyDialog] = useState(null) // { defaultName, name } | null
  // 유사 SV 찾기 모달 (Semantic View 타입 전용) — 공용 SvRecommendDialog 재사용
  const [recommendOpen, setRecommendOpen] = useState(false)
  const [copying, setCopying] = useState(false)
  const [saving, setSaving] = useState(false)
  const [clipCopied, setClipCopied] = useState(false)

  // 인라인 이름 변경 (본인 항목만 허용)
  const [renamingKey, setRenamingKey] = useState(null) // `${userId}::${name}` 또는 null
  const [renameValue, setRenameValue] = useState('')

  // 검색 / 필터
  const [query, setQuery] = useState('')
  const [mineOnly, setMineOnly] = useState(false)

  // 모든 사용자 (본인은 항상 맨 위 + "나" 뱃지)
  // 백엔드 응답에 본인이 빠져 있어도 myUser prop 으로 보강.
  const orderedUsers = useMemo(() => {
    const valid = users.filter(u => u.user_id)
    const others = valid.filter(u => u.user_id !== myUserId)
    const meFromList = valid.find(u => u.user_id === myUserId)
    const me = myUserId
      ? [meFromList || {
          user_id: myUserId,
          last_name: myUser?.last_name || '',
          first_name: myUser?.first_name || '',
          email: myUser?.email || '',
        }]
      : []
    return [...me, ...others]
  }, [users, myUserId, myUser])

  const userById = useMemo(() => {
    const m = new Map()
    for (const u of orderedUsers) m.set(u.user_id, u)
    return m
  }, [orderedUsers])

  // 사용자 목록 로드
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true)
    try {
      const list = await api.listExploreUsers(storageSettings)
      setUsers(list || [])
    } catch (e) {
      showToast?.('error', '사용자 목록 조회 실패: ' + e.message)
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }, [storageSettings, showToast])

  useEffect(() => { loadUsers() }, [loadUsers])

  // 모든 사용자 × 지정 타입의 뷰 목록을 단일 집계 엔드포인트로 로드
  const loadAllViewsFor = useCallback(async (type) => {
    setLoadingType(prev => ({ ...prev, [type]: true }))
    try {
      // user_id 비워서 호출 (백엔드는 모든 user scope 를 순회)
      const list = await api.listExploreAllViews(storageSettings, type)
      setViewsByType(prev => ({ ...prev, [type]: list || [] }))
    } catch (e) {
      setViewsByType(prev => ({ ...prev, [type]: [] }))
      showToast?.('error', '뷰 목록 조회 실패: ' + e.message)
    } finally {
      setLoadingType(prev => ({ ...prev, [type]: false }))
    }
  }, [storageSettings, showToast])

  // viewType 이 바뀌거나 storage 바뀌었을 때 캐시 미스면 1회 로드
  const inFlightRef = useRef(new Set())
  useEffect(() => {
    if (viewsByType[viewType] !== undefined) return
    if (inFlightRef.current.has(viewType)) return
    inFlightRef.current.add(viewType)
    loadAllViewsFor(viewType).finally(() => {
      inFlightRef.current.delete(viewType)
    })
  }, [viewType, viewsByType, loadAllViewsFor])

  // 평탄화 + 정렬: 본인 작성분 먼저, 그 다음 작성자명, 같은 작성자면 뷰 이름순
  // 응답이 user_id 만 갖고 있으므로 userById 로 작성자 표시명을 조인.
  const aggregatedViews = useMemo(() => {
    const list = viewsByType[viewType] || []
    const items = list.map(v => {
      const u = userById.get(v.user_id)
      const ownerName = u
        ? `${u.last_name || ''}${u.first_name || ''}`.trim() || v.user_id
        : v.user_id
      return {
        ...v,
        _ownerId: v.user_id,
        _ownerName: ownerName,
        _ownerEmail: u?.email || '',
        _isMine: v.user_id === myUserId,
      }
    })
    items.sort((a, b) => {
      if (a._isMine !== b._isMine) return a._isMine ? -1 : 1
      const oc = a._ownerName.localeCompare(b._ownerName, 'ko')
      if (oc !== 0) return oc
      return (a.name || '').localeCompare(b.name || '', 'ko')
    })
    return items
  }, [viewType, viewsByType, userById, myUserId])

  // 검색 + "내 것만" 필터 적용된 view 목록
  const filteredViews = useMemo(() => {
    let arr = aggregatedViews
    if (mineOnly && myUserId) {
      arr = arr.filter(v => v._isMine)
    }
    return filterByText(arr, query, ['name', 'description', '_ownerName', '_ownerId'])
  }, [aggregatedViews, query, mineOnly, myUserId])

  // 사이드바 헤더 로딩 표시
  const anyLoading = useMemo(
    () => loadingUsers || Object.values(loadingType).some(Boolean),
    [loadingUsers, loadingType]
  )

  // dirty 판정 — editedView 와 selectedView 의 텍스트 필드 비교
  const dirtyTabKeys = useMemo(() => {
    if (!selectedView || !editedView) return new Set()
    const tabs = availablePreviewTabs(selectedView, selectedKey?.viewType)
    const set = new Set()
    for (const t of tabs) {
      if ((editedView[t.key] ?? '') !== (selectedView[t.key] ?? '')) set.add(t.key)
    }
    return set
  }, [editedView, selectedView, selectedKey])
  const isDirty = dirtyTabKeys.size > 0

  // 항목 상세 로드 (semantic / composite / sql)
  const handleSelectView = useCallback(async (userId, type, name) => {
    if (isDirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 버리고 이동할까요?')) return
    setSelectedKey({ userId, viewType: type, name })
    setSelectedView(null)
    setEditedView(null)
    setLoadingDetail(true)
    try {
      // 코드 리뷰: 유사 SV 추천의 _shared scope 후보는 owner sentinel '_shared' 로
      // 들어온다 — storage user_id 로 쓰면 'user:_shared' 가 되므로 owner_user_id
      // 쿼리로 전달해 백엔드가 _shared scope 를 직접 열게 한다.
      const isShared = userId === '_shared'
      const otherStorage = { ...storageSettings, user_id: isShared ? '' : userId }
      const ownerArg = isShared ? '_shared' : ''
      const data = type === 'composite'
        ? await api.getCompositeView(name, otherStorage, ownerArg)
        : type === 'sql'
        ? await api.getSqlQuery(name, otherStorage, ownerArg)
        : await api.getView(name, otherStorage, ownerArg)
      setSelectedView(data)
      setEditedView(data ? { ...data } : null)
      const tabs = availablePreviewTabs(data, type)
      if (tabs.length > 0) setPreviewTab(tabs[0].key)
    } catch (e) {
      showToast?.('error', '상세 조회 실패: ' + e.message)
    } finally {
      setLoadingDetail(false)
    }
  }, [storageSettings, showToast, isDirty])

  // 새로고침 — 사용자 + 현재 viewType 집계 캐시를 비우고 재로드
  const refreshAll = useCallback(async () => {
    if (isDirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 버리고 새로고침할까요?')) return
    setViewsByType(prev => {
      const next = { ...prev }
      delete next[viewType]
      return next
    })
    await Promise.all([loadUsers(), loadAllViewsFor(viewType)])
  }, [viewType, loadUsers, loadAllViewsFor, isDirty])

  const switchViewType = useCallback((type) => {
    if (type === viewType) return
    if (isDirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 버리고 이동할까요?')) return
    setViewType(type)
  }, [viewType, isDirty])

  // 복사 — 내 워크스페이스에 충돌 없는 이름 결정
  const openCopyDialog = useCallback(async () => {
    if (!selectedView || !selectedKey) return
    const baseName = selectedView.name || selectedKey.name
    const suggestedBase = `${baseName}__from_${selectedKey.userId}`
    let candidate = suggestedBase
    try {
      const myStorage = { ...storageSettings, user_id: myUserId }
      const myList = selectedKey.viewType === 'composite'
        ? await api.listCompositeViews(myStorage)
        : selectedKey.viewType === 'sql'
        ? await api.listSqlQueries(myStorage)
        : await api.listViews(myStorage)
      const existing = new Set((myList || []).map(v => v.name))
      let i = 2
      while (existing.has(candidate)) {
        candidate = `${suggestedBase}_${i}`
        i++
      }
    } catch {
      // 목록 조회 실패해도 기본 이름으로 진행
    }
    setCopyDialog({ defaultName: candidate, name: candidate })
  }, [selectedView, selectedKey, storageSettings, myUserId])

  const handleCopy = useCallback(async () => {
    if (!copyDialog || !selectedView || !selectedKey) return
    const newName = copyDialog.name.trim()
    if (!newName) { showToast?.('warning', '이름을 입력하세요.'); return }
    setCopying(true)
    try {
      const myStorage = { ...storageSettings, user_id: myUserId }
      const data = { ...selectedView, name: newName }
      if (selectedKey.viewType === 'composite') {
        await api.saveCompositeView(data, myStorage)
      } else if (selectedKey.viewType === 'sql') {
        await api.saveSqlQuery(data, myStorage)
      } else {
        await api.saveView(data, myStorage)
      }
      showToast?.('success', `'${newName}' 내 워크스페이스에 복사 완료`)
      setCopyDialog(null)
    } catch (e) {
      showToast?.('error', '복사 실패: ' + e.message)
    } finally {
      setCopying(false)
    }
  }, [copyDialog, selectedView, selectedKey, storageSettings, myUserId, showToast])

  const previewTabs = useMemo(
    () => selectedView ? availablePreviewTabs(selectedView, selectedKey?.viewType) : [],
    [selectedView, selectedKey]
  )
  const activePreviewTab = useMemo(() => {
    if (!previewTabs.length) return null
    return previewTabs.find(t => t.key === previewTab) || previewTabs[0]
  }, [previewTabs, previewTab])
  const previewContent = useMemo(() => {
    if (!editedView || !activePreviewTab) return ''
    return editedView[activePreviewTab.key] || ''
  }, [editedView, activePreviewTab])

  const handleEditorChange = useCallback((val) => {
    if (!activePreviewTab) return
    setEditedView(prev => prev ? { ...prev, [activePreviewTab.key]: val } : prev)
  }, [activePreviewTab])

  const handleRevert = useCallback(() => {
    if (!isDirty) return
    if (!window.confirm('편집 내용을 모두 원본으로 되돌릴까요?')) return
    setEditedView(selectedView ? { ...selectedView } : null)
  }, [isDirty, selectedView])

  const handleSave = useCallback(async () => {
    if (!editedView || !selectedKey || !isDirty) return
    // 코드 리뷰: _shared 항목은 Explore 에서 원본 저장 불가 (user_id 기반 저장
    // 경로가 'user:_shared' 오염을 만든다) — 내 워크스페이스로 복사만 허용.
    if (selectedKey.userId === '_shared') {
      showToast?.('error', "공유(_shared) 항목은 여기서 저장할 수 없습니다 — '내 워크스페이스로 복사'를 사용하세요.")
      return
    }
    // KBNL-96: SVS admin 만 다른 사용자 scope 에 저장 가능. 비-admin 의
    // cross-user save 는 백엔드에서도 403, UI 에서도 즉시 차단.
    if (selectedKey.userId !== myUserId && !isAdmin) {
      showToast?.('error', '다른 사용자 컨텐츠는 SVS 관리자만 수정할 수 있습니다.')
      return
    }
    setSaving(true)
    try {
      // 원본 소유자 스코프에 저장 (본인이면 myUserId 와 동일)
      const ownerStorage = { ...storageSettings, user_id: selectedKey.userId }
      const data = { ...editedView, name: selectedKey.name }
      if (selectedKey.viewType === 'composite') {
        await api.saveCompositeView(data, ownerStorage)
      } else if (selectedKey.viewType === 'sql') {
        await api.saveSqlQuery(data, ownerStorage)
      } else {
        await api.saveView(data, ownerStorage)
      }
      // 저장 성공 → 원본 = 현재 작업본 (dirty 해제)
      setSelectedView(data)
      setEditedView({ ...data })
      showToast?.('success', `'${selectedKey.name}' 저장 완료`)
    } catch (e) {
      showToast?.('error', '저장 실패: ' + e.message)
    } finally {
      setSaving(false)
    }
  }, [editedView, selectedKey, isDirty, storageSettings, showToast, myUserId, isAdmin])

  // KBNL-96: 삭제 — 본인 항목은 항상 가능, 다른 사용자 항목은 SVS admin 만.
  const handleDeleteSelected = useCallback(async () => {
    if (!selectedKey) return
    // 코드 리뷰: _shared 항목은 Explore 삭제 경로(user_id 기반)가 scope 를 오염시키므로 차단.
    if (selectedKey.userId === '_shared') {
      showToast?.('error', '공유(_shared) 항목은 여기서 삭제할 수 없습니다.')
      return
    }
    if (selectedKey.userId !== myUserId && !isAdmin) {
      showToast?.('error', '다른 사용자 컨텐츠는 SVS 관리자만 삭제할 수 있습니다.')
      return
    }
    if (!window.confirm(`'${selectedKey.name}' 을(를) 삭제할까요?`)) return
    try {
      const ownerStorage = { ...storageSettings, user_id: selectedKey.userId }
      if (selectedKey.viewType === 'composite') {
        await api.deleteCompositeView(selectedKey.name, ownerStorage)
      } else if (selectedKey.viewType === 'sql') {
        await api.deleteSqlQuery(selectedKey.name, ownerStorage)
      } else {
        await api.deleteView(selectedKey.name, ownerStorage)
      }
      showToast?.('success', `'${selectedKey.name}' 삭제 완료`)
      // 캐시에서 해당 항목 제거 + 선택 해제
      const removed = { userId: selectedKey.userId, viewType: selectedKey.viewType, name: selectedKey.name }
      setSelectedKey(null)
      setSelectedView(null)
      setEditedView(null)
      setViewsByType(prev => {
        const list = prev[removed.viewType] || []
        return { ...prev, [removed.viewType]: list.filter(v => !(v._ownerId === removed.userId && v.name === removed.name)) }
      })
    } catch (e) {
      showToast?.('error', '삭제 실패: ' + e.message)
    }
  }, [selectedKey, myUserId, isAdmin, storageSettings, showToast])

  // ── 인라인 이름 변경 ──────────────────────────────────────────────
  const handleStartRename = useCallback((item) => {
    // KBNL-96: 본인 항목 또는 SVS admin 만 rename 가능.
    if (!item || (!item._isMine && !isAdmin)) return
    setRenamingKey(`${item._ownerId}::${item.name}`)
    setRenameValue(item.name)
  }, [isAdmin])

  const handleCancelRename = useCallback(() => {
    setRenamingKey(null)
  }, [])

  const handleConfirmRename = useCallback(async () => {
    const key = renamingKey
    if (!key) return
    const [ownerId, oldName] = key.split('::')
    const newName = renameValue.trim()
    if (!newName || newName === oldName) {
      setRenamingKey(null)
      return
    }
    // KBNL-96: 본인 항목 또는 SVS admin 만 rename 가능.
    if (ownerId !== myUserId && !isAdmin) {
      showToast?.('error', '다른 사용자 컨텐츠는 SVS 관리자만 이름 변경할 수 있습니다.')
      setRenamingKey(null)
      return
    }
    // 같은 사용자 안에서 중복 검사
    const myList = (viewsByType[viewType] || []).filter(v => v.user_id === myUserId)
    if (myList.some(v => v.name === newName)) {
      showToast?.('error', `이미 '${newName}' 이름이 존재합니다.`)
      setRenamingKey(null)
      return
    }
    try {
      // KBNL-96: admin 의 cross-user rename 도 처리 — ownerId 기준 storage 사용.
      const ownerStorage = { ...storageSettings, user_id: ownerId }
      if (viewType === 'composite') await api.renameCompositeView(oldName, newName, ownerStorage)
      else if (viewType === 'sql') await api.renameSqlQuery(oldName, newName, ownerStorage)
      else await api.renameView(oldName, newName, ownerStorage)
      // 로컬 캐시 즉시 갱신 — 전체 새로고침 회피. 비교 키를 ownerId 로 (admin 도 정상 반영).
      setViewsByType(prev => {
        const list = prev[viewType] || []
        const next = list.map(v =>
          (v.user_id === ownerId && v.name === oldName) ? { ...v, name: newName } : v
        )
        return { ...prev, [viewType]: next }
      })
      // 현재 선택된 항목이면 selectedKey 동기화
      if (selectedKey && selectedKey.userId === ownerId && selectedKey.name === oldName && selectedKey.viewType === viewType) {
        setSelectedKey({ ...selectedKey, name: newName })
        if (selectedView) setSelectedView({ ...selectedView, name: newName })
        if (editedView)   setEditedView({ ...editedView, name: newName })
      }
      showToast?.('success', `'${oldName}' → '${newName}' 이름 변경 완료`)
    } catch (e) {
      showToast?.('error', '이름 변경 실패: ' + e.message)
    } finally {
      setRenamingKey(null)
    }
  }, [renamingKey, renameValue, viewType, viewsByType, myUserId, isAdmin, storageSettings, selectedKey, selectedView, editedView, showToast])

  const handleClipboardCopy = useCallback(() => {
    if (!previewContent) return
    copyToClipboard(previewContent).then(() => {
      setClipCopied(true)
      setTimeout(() => setClipCopied(false), 1500)
    }).catch(() => {
      showToast?.('error', '클립보드 복사 실패')
    })
  }, [previewContent, showToast])

  // 탭 전환/뷰 변경 시 복사됨 뱃지 리셋
  useEffect(() => { setClipCopied(false) }, [previewTab, selectedKey])

  const previewExtensions = useMemo(() => {
    const langExt = activePreviewTab ? (LANG_EXT[activePreviewTab.lang]?.() ?? []) : []
    return [
      ...langExt,
      search({ top: true }),
      Prec.highest(keymap.of([
        ...searchKeymap,
        { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
      ])),
    ]
  }, [activePreviewTab])

  return (
    <div className="explore-page">
      {/* 좌측: 평탄화 뷰 목록 */}
      <aside className="explore-tree-pane">
        <div className="explore-pane-header">
          <div className="explore-view-tabs">
            <button
              className={`mode-btn ${viewType === 'semantic' ? 'active' : ''}`}
              onClick={() => switchViewType('semantic')}
            >Semantic</button>
            <button
              className={`mode-btn ${viewType === 'composite' ? 'active' : ''}`}
              onClick={() => switchViewType('composite')}
            >Composite</button>
            <button
              className={`mode-btn ${viewType === 'sql' ? 'active' : ''}`}
              onClick={() => switchViewType('sql')}
            >SQL</button>
          </div>
          <div style={{ flex: 1 }} />
          <span className="explore-count">{aggregatedViews.length}</span>
          <button className="btn-link" onClick={refreshAll} disabled={anyLoading}>
            {anyLoading ? '...' : '새로고침'}
          </button>
        </div>
        {/* 검색 + 내 것만 토글 */}
        <div className="explore-search-bar">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="이름 / 설명 / 소유자 (Ctrl+K)"
            total={aggregatedViews.length}
            matched={filteredViews.length}
            compact
          >
            {myUserId && (
              <button
                type="button"
                className={`search-toggle-btn ${mineOnly ? 'is-on' : ''}`}
                onClick={() => setMineOnly(v => !v)}
                title={mineOnly ? '내 것만 보기 OFF' : '내 것만 보기 ON'}
              >👤 나</button>
            )}
          </SearchInput>
        </div>
        <div className="explore-flat-list">
          {loadingUsers && orderedUsers.length === 0 && (
            <div className="explore-empty">사용자 목록 로딩 중...</div>
          )}
          {!loadingUsers && orderedUsers.length === 0 && (
            <div className="explore-empty">사용자가 없습니다.</div>
          )}
          {orderedUsers.length > 0 && loadingType[viewType] && aggregatedViews.length === 0 && (
            <div className="explore-empty">
              {viewType === 'composite' ? 'Composite' : viewType === 'sql' ? 'SQL' : 'Semantic'} 뷰 목록 로딩 중...
            </div>
          )}
          {!loadingUsers && orderedUsers.length > 0 && aggregatedViews.length === 0 && !anyLoading && (
            <div className="explore-empty">저장된 뷰가 없습니다.</div>
          )}
          {aggregatedViews.length > 0 && filteredViews.length === 0 && (
            <div className="explore-empty">조건에 맞는 뷰가 없습니다.</div>
          )}
          {filteredViews.map(v => {
            const isSelected =
              selectedKey &&
              selectedKey.userId === v._ownerId &&
              selectedKey.viewType === viewType &&
              selectedKey.name === v.name
            const itemKey = `${v._ownerId}::${v.name}`
            const isRenaming = renamingKey === itemKey
            const tooltip = [
              `이름: ${v._ownerName}`,
              `사번: ${v._ownerId}`,
              v._ownerEmail ? `이메일: ${v._ownerEmail}` : null,
              v._isMine ? '(더블클릭으로 이름 변경)' : null,
            ].filter(Boolean).join('\n')
            return (
              <div
                key={itemKey}
                className={`explore-view-item ${isSelected ? 'active' : ''}`}
                onClick={() => { if (!isRenaming) handleSelectView(v._ownerId, viewType, v.name) }}
                onDoubleClick={() => handleStartRename(v)}
                title={v._isMine ? '더블클릭으로 이름 변경' : undefined}
              >
                <div className="explore-view-name-row">
                  {isRenaming ? (
                    <input
                      autoFocus
                      className="explore-rename-input"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleConfirmRename()
                        if (e.key === 'Escape') handleCancelRename()
                      }}
                      onBlur={handleConfirmRename}
                      onClick={e => e.stopPropagation()}
                      onDoubleClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <span className="explore-view-name">
                      <HighlightText text={v.name} query={query} />
                    </span>
                  )}
                  <span className="explore-view-owner" title={tooltip}>
                    <HighlightText text={v._ownerName} query={query} />
                    {v._isMine && <span className="explore-user-badge">나</span>}
                  </span>
                </div>
                {!isRenaming && v.description && (
                  <div className="explore-view-desc">
                    <HighlightText text={v.description} query={query} />
                  </div>
                )}
                <div className="explore-view-meta">
                  {v.project_id && <span>{v.project_id}</span>}
                  {v.dataset_id && <span>· {v.dataset_id}</span>}
                  {v.updated_at && <span>· {String(v.updated_at).slice(0, 10)}</span>}
                  <TokenBadge count={v.token_count} />
                </div>
              </div>
            )
          })}
        </div>
      </aside>

      {/* 우측: 미리보기 */}
      <section className="explore-preview-pane">
        <div className="explore-pane-header">
          <span className="explore-preview-title">
            {selectedView ? (selectedView.name || selectedKey?.name) : '미리보기'}
            {isDirty && <span className="explore-modified-dot" title="수정됨 (저장되지 않음)" />}
          </span>
          {selectedKey && (
            <span className="explore-preview-owner">
              by {userById.get(selectedKey.userId)
                ? `${userById.get(selectedKey.userId).last_name || ''}${userById.get(selectedKey.userId).first_name || ''}`.trim()
                  || selectedKey.userId
                : selectedKey.userId}
            </span>
          )}
          {isDirty && <span className="explore-modified-label">수정됨</span>}
          <div style={{ flex: 1 }} />
          {/* 유사 SV 찾기 — Semantic View 타입에서만 (SV 탭/워크벤치와 동일 추천 모달) */}
          {selectedKey && selectedKey.viewType === 'semantic' && (
            <button
              className="btn-secondary"
              disabled={!selectedView || loadingDetail}
              onClick={() => setRecommendOpen(true)}
              title={`'${selectedKey.name}' 와(과) 유사한 Semantic View 찾기`}
            >
              🔍 유사 SV 찾기
            </button>
          )}
          {selectedKey && (
            <>
              <button
                className="btn-secondary"
                disabled={!isDirty || saving}
                onClick={handleRevert}
                title="원본으로 되돌리기"
              >
                되돌리기
              </button>
              <button
                className="btn-primary"
                disabled={!isDirty || saving}
                onClick={handleSave}
                title={selectedKey.userId === myUserId ? '내 워크스페이스에 저장' : `'${selectedKey.userId}' 스코프에 저장 (admin)`}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </>
          )}
          {/* KBNL-96: 삭제 — 본인 항목 또는 SVS admin 인 경우 노출 */}
          {selectedKey && (selectedKey.userId === myUserId || isAdmin) && (
            <button
              className="btn-danger"
              disabled={!selectedView || saving}
              onClick={handleDeleteSelected}
              title={selectedKey.userId === myUserId ? '내 항목 삭제' : '다른 사용자 항목 삭제 (admin)'}
            >
              삭제
            </button>
          )}
          {selectedKey && selectedKey.userId !== myUserId && (
            <button
              className="btn-primary"
              disabled={!selectedView || copying}
              onClick={openCopyDialog}
            >
              내 워크스페이스로 복사
            </button>
          )}
        </div>

        {!selectedView && !loadingDetail && (
          <div className="explore-empty" style={{ padding: 24 }}>
            왼쪽 목록에서 뷰를 선택하세요.
          </div>
        )}
        {loadingDetail && <div className="explore-empty" style={{ padding: 24 }}>로딩 중...</div>}

        {selectedView && (
          <>
            {(selectedView.updated_at || selectedView.created_at) && (
              <div
                className="explore-preview-meta"
                title={[
                  selectedView.created_at ? `생성: ${selectedView.created_at}` : null,
                  selectedView.updated_at ? `수정: ${selectedView.updated_at}` : null,
                ].filter(Boolean).join('\n')}
              >
                <span>
                  마지막 저장 {formatTimestamp(selectedView.updated_at || selectedView.created_at)}
                  <span className="explore-preview-meta-note"> · 문서 전체 기준</span>
                </span>
              </div>
            )}
            {selectedView.description && (
              <div className="explore-preview-desc">{selectedView.description}</div>
            )}
            <div className="explore-preview-tabs">
              {previewTabs.map(t => (
                <button
                  key={t.key}
                  className={`tab-btn ${previewTab === t.key ? 'active' : ''}`}
                  onClick={() => setPreviewTab(t.key)}
                >
                  {t.label}
                  {dirtyTabKeys.has(t.key) && <span className="explore-modified-dot" title="수정됨" />}
                </button>
              ))}
              <div style={{ flex: 1 }} />
              <button
                className="btn-copy"
                onClick={handleClipboardCopy}
                disabled={!previewContent}
                title="클립보드에 복사"
              >
                {clipCopied ? '✓ 복사됨' : '복사'}
              </button>
            </div>
            <div className="explore-preview-code">
              {previewContent ? (
                <CodeMirror
                  value={previewContent}
                  height="100%"
                  theme={editorTheme || oneDark}
                  extensions={previewExtensions}
                  onChange={handleEditorChange}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    autocompletion: false,
                  }}
                />
              ) : (
                <div className="explore-empty" style={{ padding: 24 }}>(내용 없음)</div>
              )}
            </div>
          </>
        )}

        {/* 유사 SV 찾기 모달 — 4개 화면(SV 탭/SQL 변환기/워크벤치/컴포지트)과 공용인
            SvRecommendDialog 의 5번째 사용처. seed 는 선택된 SV 의 저장본
            (scope 힌트 'user:{owner}' 로 정확 로드). 후보 선택 시 Explore 안에서
            그 SV 를 바로 연다 (owner 포함 — 같은 이름 다른 소유자 정확 매칭).
            주의: _shared 항목의 scope 는 'user:_shared' 가 아니라 '_shared' — 잘못
            주면 seed 로드가 전체 탐색 fallthrough 로 넘어가 동명 user:* doc 을
            집을 수 있어 아래 seedSvScope 에서 분기한다. */}
        <SvRecommendDialog
          open={recommendOpen}
          onClose={() => setRecommendOpen(false)}
          mode="seed_sv"
          seedSvName={selectedKey?.name || ''}
          seedSvScope={selectedKey
            ? (selectedKey.userId === '_shared' ? '_shared' : `user:${selectedKey.userId}`)
            : ''}
          storageSettings={storageSettings}
          showToast={showToast}
          editorTheme={editorTheme}
          onSelect={(c) => {
            if (c?.name) handleSelectView(c.owner_user_id || c.user_id || '', 'semantic', c.name)
          }}
          title={`'${selectedKey?.name || ''}' 와(과) 유사한 Semantic View`}
        />

        {/* 복사 다이얼로그 */}
        {copyDialog && (
          <div className="explore-copy-overlay" onClick={() => !copying && setCopyDialog(null)}>
            <div className="explore-copy-dialog" onClick={e => e.stopPropagation()}>
              <h3>내 워크스페이스로 복사</h3>
              <div className="form-group">
                <label>새 이름</label>
                <input
                  className="field"
                  value={copyDialog.name}
                  onChange={e => setCopyDialog(d => ({ ...d, name: e.target.value }))}
                  autoFocus
                />
                <div className="form-hint">
                  내 user_id <strong>{myUserId || '(없음)'}</strong> 스코프에 저장됩니다.
                </div>
              </div>
              <div className="explore-copy-actions">
                <button className="btn-secondary" onClick={() => setCopyDialog(null)} disabled={copying}>취소</button>
                <button className="btn-primary" onClick={handleCopy} disabled={copying}>
                  {copying ? '복사 중...' : '복사'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

// ISO 타임스탬프 → "YYYY-MM-DD HH:mm" (실패 시 원문 반환)
function formatTimestamp(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  if (isNaN(d.getTime())) return String(ts)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// 비활성화된 미리보기 탭 (재활성화 시 해당 key를 제거)
// semantic 모드: yaml_string=Metric YAML, bq_mv=BQ MV, published_context=BQ Agent Context
// KBNL-75 transition: backend 가 신규 (composite_yaml/metric_view_yaml) + legacy 'yaml_string' 양쪽 mirror 하므로
// 'yaml_string' 키 그대로 사용해도 모든 doc 에서 동작. Phase 3 (legacy 제거) 시점에 키 rename 필요.
const DISABLED_PREVIEW_KEYS = new Set(['yaml_string', 'bq_mv', 'published_context'])

// 뷰 타입에 따라 표시 가능한 미리보기 탭 목록
function availablePreviewTabs(view, viewType) {
  const candidates = viewType === 'composite'
    ? [
        { key: 'yaml_string', label: 'Composite YAML', lang: 'yaml' },
        { key: 'sql_text', label: 'SQL', lang: 'sql' },
      ]
    : viewType === 'sql'
    ? [
        { key: 'sql_text', label: 'SQL', lang: 'sql' },
        { key: 'semantic_yaml', label: 'Semantic YAML', lang: 'yaml' },
      ]
    : [
        { key: 'semantic_yaml', label: 'Semantic YAML', lang: 'yaml' },
        { key: 'yaml_string', label: 'Metric YAML', lang: 'yaml' },
        { key: 'sql_text', label: 'SQL', lang: 'sql' },
        { key: 'ddl', label: 'DDL', lang: 'sql' },
        { key: 'bq_view', label: 'BigQuery View', lang: 'sql' },
        { key: 'bq_mv', label: 'BQ MV', lang: 'sql' },
        { key: 'lookml', label: 'LookML', lang: 'lookml' },
        { key: 'published_context', label: 'Context', lang: 'yaml' },
      ]
  return candidates.filter(t => {
    // composite 모드의 yaml_string(Composite YAML)은 숨김 대상이 아니다 — semantic 모드에서만 적용.
    if (viewType !== 'composite' && DISABLED_PREVIEW_KEYS.has(t.key)) return false
    const v = view[t.key]
    return v && typeof v === 'string' && v.trim().length > 0
  })
}
